import { useEffect, useState } from "react";
import axios from "axios";
import { X, ArrowRight } from "lucide-react";
import OfferCountdown from "./OfferCountdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TODAY_KEY = () => `offer_popup_shown_${new Date().toISOString().slice(0, 10)}`;

/**
 * Once-per-day marketing popup shown on the public storefront.
 * Skipped if already dismissed today (localStorage flag rolls at midnight).
 * Admin controls image, headline, subline, CTA, and optional countdown timer
 * via /admin/offer-popup settings. When the timer expires the popup auto-hides.
 */
export default function OfferPopup() {
  const [data, setData] = useState(null);

  useEffect(() => {
    // Skip if user already dismissed it today
    if (localStorage.getItem(TODAY_KEY()) === "1") return;
    axios.get(`${API}/offer-popup`).then((r) => {
      if (r.data?.enabled && (r.data.image_url || r.data.headline)) {
        // If expires_at is already past, backend returns enabled:false — but double-check client-side too
        if (r.data.expires_at) {
          const t = new Date(r.data.expires_at).getTime();
          if (!Number.isNaN(t) && t <= Date.now()) return;
        }
        // Small delay so the popup doesn't feel intrusive on landing
        setTimeout(() => setData(r.data), 1500);
      }
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    localStorage.setItem(TODAY_KEY(), "1");
    setData(null);
  };

  const ctaClick = () => {
    dismiss();
    if (data?.cta_link) window.location.href = data.cta_link;
  };

  if (!data) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={dismiss} data-testid="offer-popup">
      <div className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
          data-testid="offer-popup-close"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        {data.image_url && (
          <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
            <img
              src={data.image_url}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
              data-testid="offer-popup-image"
            />
          </div>
        )}
        <div className="p-6 text-center">
          {data.headline && (
            <div className="font-display text-2xl md:text-3xl font-black tracking-tighter text-zinc-900 leading-tight" data-testid="offer-popup-headline">
              {data.headline}
            </div>
          )}
          {data.subline && (
            <p className="mt-2 text-sm text-zinc-600" data-testid="offer-popup-subline">{data.subline}</p>
          )}
          {data.expires_at && (
            <OfferCountdown
              expiresAt={data.expires_at}
              style={data.countdown_style || "inline"}
              onExpire={() => setData(null)}
            />
          )}
          {data.cta_label && data.cta_link && (
            <button
              onClick={ctaClick}
              className="mt-5 inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#e65a00] text-white font-bold px-6 py-3 rounded-full text-sm shadow-lg transition-all hover:scale-105"
              data-testid="offer-popup-cta"
            >
              {data.cta_label} <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
